"use client";

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { CUSTOMER_TYPE_IDS } from "@/lib/constants/customer-types";

interface ImportTemplatesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  venueId: number;
  showId: number;
  /** Called after a successful import so the parent can refresh its list. */
  onImported: () => void;
}

interface ShowOption {
  id: number;
  name: string;
}

const getCustomerTypeLabel = (customerTypeId?: number): string | null => {
  if (customerTypeId === CUSTOMER_TYPE_IDS.PRIVATE) return "Private";
  if (customerTypeId === CUSTOMER_TYPE_IDS.CORPORATE) return "Corporate";
  return null;
};

const getShowName = (show: any): string => {
  const infos = show?.show_infos || [];
  const preferred =
    infos.find((i: any) => i.lang === "en")?.name || infos[0]?.name;
  const name = preferred || `Show #${show?.id}`;

  // Append the customer type, e.g. "Catchup - Private"
  const typeLabel = getCustomerTypeLabel(show?.customer_type_id);
  return typeLabel ? `${name} - ${typeLabel}` : name;
};

export function ImportTemplatesDialog({
  open,
  onOpenChange,
  venueId,
  showId,
  onImported,
}: ImportTemplatesDialogProps) {
  const [shows, setShows] = useState<ShowOption[]>([]);
  const [isLoadingShows, setIsLoadingShows] = useState(false);
  const [selectedShowId, setSelectedShowId] = useState<string>("");
  const [isImporting, setIsImporting] = useState(false);

  // Load the venue's other shows when the dialog opens
  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    const fetchShows = async () => {
      setIsLoadingShows(true);
      try {
        const response = await fetch(`/api/venues/${venueId}/shows`);
        if (!response.ok) {
          throw new Error("Failed to fetch shows");
        }
        const { shows: fetchedShows } = await response.json();

        const options: ShowOption[] = (fetchedShows || [])
          .filter((show: any) => show.id !== showId)
          .map((show: any) => ({ id: show.id, name: getShowName(show) }))
          .sort((a: ShowOption, b: ShowOption) => a.name.localeCompare(b.name));

        if (!cancelled) {
          setShows(options);
        }
      } catch (error) {
        console.error("Error fetching shows for import:", error);
        if (!cancelled) {
          toast.error("Failed to load shows");
        }
      } finally {
        if (!cancelled) {
          setIsLoadingShows(false);
        }
      }
    };

    // Reset selection each time the dialog is opened
    setSelectedShowId("");
    fetchShows();

    return () => {
      cancelled = true;
    };
  }, [open, venueId, showId]);

  const handleImport = async () => {
    if (!selectedShowId) {
      toast.error("Please select a show to import from");
      return;
    }

    setIsImporting(true);
    try {
      const response = await fetch(
        `/api/venues/${venueId}/shows/${showId}/email-templates/import`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sourceShowId: parseInt(selectedShowId) }),
        },
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.error || "Failed to import templates");
      }

      toast.success(
        `Imported ${result.imported} email template${
          result.imported === 1 ? "" : "s"
        }`,
      );
      onOpenChange(false);
      onImported();
    } catch (error) {
      console.error("Error importing templates:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to import templates",
      );
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Import email templates</DialogTitle>
          <DialogDescription>
            Copy the email templates from another show in this venue. This
            replaces the current show&apos;s templates with copies of the
            selected show&apos;s subject, body and settings.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            Import from
          </label>
          <Select
            value={selectedShowId}
            onValueChange={setSelectedShowId}
            disabled={isLoadingShows || isImporting}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={
                  isLoadingShows ? "Loading shows..." : "Select a show"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {shows.map((show) => (
                <SelectItem key={show.id} value={show.id.toString()}>
                  {show.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {!isLoadingShows && shows.length === 0 && (
            <p className="text-sm text-gray-500 mt-2">
              No other shows are available in this venue.
            </p>
          )}
        </div>

        <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          <strong>Note:</strong> For an accurate, exact copy, make sure the
          selected show is the same type (Private / Corporate). Otherwise some
          templates may not match and may need some adjustment.
        </p>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isImporting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={!selectedShowId || isImporting}
          >
            {isImporting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
